import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS } from "../../data/content";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -10 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      data-testid="main-navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? "bg-ink/80 backdrop-blur-md border-b border-white/10 py-4"
          : "bg-transparent py-6"
      }`}
    >
      <nav className="container-editorial flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => go("#hero")}
          className="group flex items-baseline gap-2 text-left"
        >
          <span className="font-serif text-2xl leading-none tracking-tight text-bone">
            Dakka<span className="text-gold"> Gabriel Rey</span>
          </span>
        </button>

        <div className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-${l.label.toLowerCase()}-link`}
              onClick={() => go(l.href)}
              className="relative text-sm text-ash hover:text-bone transition-colors duration-300 tracking-wide group"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-gold transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-connect-cta"
            onClick={() => go("#contact")}
            className="hidden sm:inline-flex items-center gap-1.5 bg-gold text-ink px-5 py-2.5 text-sm font-medium tracking-wide hover:bg-gold-bright transition-colors duration-300"
          >
            Connect <ArrowUpRight size={15} />
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden text-bone p-1"
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-ink/95 backdrop-blur-md border-t border-white/10 mt-4"
          >
            <div className="container-editorial py-6 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}-link`}
                  onClick={() => go(l.href)}
                  className="text-left py-3 font-serif text-2xl text-bone hover:text-gold transition-colors border-b border-white/5"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="mobile-connect-cta"
                onClick={() => go("#contact")}
                className="btn-primary mt-5"
              >
                Connect With Dakka <ArrowUpRight size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
